import { type Component, createMemo, createSignal, For, onMount, Show } from "solid-js";
import AdminEndedSessionsPage from "./AdminEndedSessionsPage";
import AdminBadge from "./admin/AdminBadge";
import AdminCard from "./admin/AdminCard";
import AdminDetailPanel from "./admin/AdminDetailPanel";
import AdminFacts from "./admin/AdminFacts";
import AdminRowName from "./admin/AdminRowName";
import { AdminEmpty, AdminError } from "./admin/AdminStatus";
import AdminTable from "./admin/AdminTable";
import { useRefreshSlot } from "./admin/refreshSlot";
import { renderEnded } from "./admin/sessionLogFormat";
import ContextMenu from "./ContextMenu";
import InlineConfirmButton from "./InlineConfirmButton";
import {
  type AdminSubjectRow,
  buildSubjectRows,
  channelCount,
  DEFAULT_SESSION_SORT,
  endedSessions,
  liveSessions,
  rowActions,
  SESSION_SORT_KEYS,
  SESSION_SORT_LABEL,
  type SessionSort,
  type SessionSortKey,
  sortSubjectRows,
} from "./lib/adminSubjectRows";
import {
  type AdminCredential,
  type AdminNetwork,
  type AdminSession,
  type AdminSessionLogEntry,
  type AdminVisitor,
  ApiError,
  adminDeleteVisitor,
  adminDisconnectSession,
  adminListCredentials,
  adminListNetworks,
  adminListSessionLogSessions,
  adminListSessions,
  adminListVisitors,
  adminReconnectSession,
  adminTerminateSession,
} from "./lib/api";
import { token } from "./lib/auth";
import { isAdminNarrow } from "./lib/theme";

// #1157 — the unified admin Sessions view. The Visitors tab is gone;
// this one lists ACTIVE AND INACTIVE sessions for both subject kinds.
//
// The row set is built in `lib/adminSubjectRows.ts` — read the reasoning
// there, it is the load-bearing part: the list is ROW-backed with live
// process state joined on top, because a registry-driven list drops
// every parked / failed / expired-but-unreaped subject out of the
// console.
//
// Column shape is dictated (vjt, 2026-08-09):
//   1. two lines — a visitor/user badge (both the SAME width, so the
//      nicks line up into a readable column) + the nick, then the
//      network underneath;
//   2. last seen;  3. channel count;  4. actions.
// Everything else moved into the per-row drill-down rather than being
// deleted: a phone cannot show those columns side by side, and the
// answer to that is fewer columns, not a table the operator has to pan.
//
// Delete lives in the drill-down, NOT in the actions cell, and says so:
// `DELETE /admin/visitors/:id` is identity-wide while a row here is one
// (subject, network) pair, so a visitor on two networks yields two rows
// whose Delete buttons would each nuke both. Behind the disclosure, and
// labelled with what it actually destroys, it stops being a footgun.
//
// #1158 item 4 — a fifth source, `/admin/session_log/sessions`. vjt ruled
// the retention fork "keep only the event" (2026-08-11): an anon visitor
// is still purged at logout and reaped at TTL, so for a session that is
// over the lifecycle log is the ONLY record left — it carries no FK to
// the subject and outlives the CASCADE. It joins on `session_id`, which
// is the row key, so it both fills in "what did this session last do"
// and supplies rows for subjects that no longer exist.
//
// #1224 — and those log-only rows are no longer IN this list. They were
// carrying a `deleted` badge and three em-dashes, because `last seen`,
// `channels` and `actions` are live-process facts and there is neither a
// process nor a row. vjt's ruling (2026-08-11): they move to a sub-page
// of Sessions with columns the record actually has, no badge, and this
// list becomes strictly live. The merge is unchanged — one row set, one
// composite key, the log still enriching a live row's drill-down — and
// `liveSessions`/`endedSessions` partition it for the two screens.
//
// Per `feedback_e2e_user_class_parity_matrix`: admin-gated EXEMPT.

// who + last seen + channels + the detail-only columns + actions. Feeds
// the detail row's colspan.
const SESSION_COLUMNS = 4;

type ActionKind = "disconnect" | "reconnect" | "terminate";

const ACTION_FN: Record<ActionKind, (token: string, id: string) => Promise<void>> = {
  disconnect: adminDisconnectSession,
  reconnect: adminReconnectSession,
  terminate: adminTerminateSession,
};

const ACTION_LABEL: Record<ActionKind, string> = {
  disconnect: "Disconnect",
  reconnect: "Reconnect",
  terminate: "Terminate",
};

function confirmKey(key: string, kind: ActionKind | "delete"): string {
  return `${key}:${kind}`;
}

// #1157 — which verbs the actions cell renders AS BUTTONS. Dictated
// (vjt, 2026-08-09): below 900px the cell holds ONE control and the
// verbs live behind a dropdown. Above it they stay side by side.
//
// A one-verb row is left alone: it already IS one control, and wrapping
// it in a menu would charge a tap to reach a list of one. So the rule
// the cell keeps at every width is "one control", not "always a menu".
//
// `armed` is the escape from the obvious bug in that: `InlineConfirmButton`
// is sticky and the PARENT owns `armed`, so once a verb is picked its
// confirmation has to be on screen — otherwise the menu would arm a
// button the operator cannot see, let alone confirm.
function cellActions(
  row: AdminSubjectRow,
  narrow: boolean,
  armed: ActionKind | null,
): ActionKind[] {
  const verbs = rowActions(row);
  if (!narrow) return verbs;
  if (armed !== null) return [armed];
  return verbs.length > 1 ? [] : verbs;
}

function renderCap(cap: number | null): string {
  // Mirrors the AdminNetworksTab cap-cell convention: `null` is the
  // "unlimited" sentinel per `Networks.update_network_settings/2`.
  return cap === null ? "∞" : String(cap);
}

const AdminSessionsTab: Component = () => {
  const [visitors, setVisitors] = createSignal<AdminVisitor[] | null>(null);
  const [credentials, setCredentials] = createSignal<AdminCredential[] | null>(null);
  const [sessions, setSessions] = createSignal<AdminSession[] | null>(null);
  const [networks, setNetworks] = createSignal<AdminNetwork[] | null>(null);
  const [logSessions, setLogSessions] = createSignal<AdminSessionLogEntry[] | null>(null);
  const [confirmingKey, setConfirmingKey] = createSignal<string | null>(null);
  const [detailKey, setDetailKey] = createSignal<string | null>(null);
  // #1224 — the sub-page is open, or the list is. Same shape as the Users
  // tab's drill-in to `AdminUserPage`, and deliberately not a route: the
  // admin console has none.
  const [endedOpen, setEndedOpen] = createSignal(false);
  const [sort, setSort] = createSignal<SessionSort>(DEFAULT_SESSION_SORT);
  // #1157 — the open row's verb dropdown, anchored to the button that
  // opened it. Anchored to the BUTTON RECT rather than the pointer so a
  // keyboard activation (clientX/Y both 0) does not open it in the
  // viewport's top-left corner.
  const [actionsMenu, setActionsMenu] = createSignal<{
    key: string;
    x: number;
    y: number;
  } | null>(null);
  const [error, setError] = createSignal<string | null>(null);
  const [loading, setLoading] = createSignal(false);

  const loaded = (): boolean =>
    visitors() !== null && credentials() !== null && logSessions() !== null;

  const rows = createMemo<AdminSubjectRow[]>(() =>
    loaded()
      ? buildSubjectRows({
          visitors: visitors() ?? [],
          credentials: credentials() ?? [],
          sessions: sessions() ?? [],
          networks: networks() ?? [],
          logSessions: logSessions() ?? [],
        })
      : [],
  );

  const live = createMemo<AdminSubjectRow[]>(() => sortSubjectRows(liveSessions(rows()), sort()));
  const ended = createMemo(() => endedSessions(rows()));

  // #1308 — picking the key already on is what reverses it, the way a
  // sortable column header behaves; picking a different one starts at
  // descending, because all three are timestamps and "newest first" is
  // what an operator wants from every one of them.
  const cycleSort = (key: SessionSortKey): void => {
    setSort((current) =>
      current.key === key
        ? { key, direction: current.direction === "desc" ? "asc" : "desc" }
        : { key, direction: "desc" },
    );
  };

  /** The verb this row is currently asking the operator to confirm. */
  const armedKind = (row: AdminSubjectRow): ActionKind | null =>
    rowActions(row).find((kind) => confirmingKey() === confirmKey(row.key, kind)) ?? null;

  /** The row shows the dropdown instead of its verbs. */
  const collapsed = (row: AdminSubjectRow): boolean =>
    isAdminNarrow() && rowActions(row).length > 1;

  const refresh = async (): Promise<void> => {
    const t = token();
    if (t === null) return;
    setLoading(true);
    setError(null);
    setConfirmingKey(null);
    // The menu names one row's verbs; the refresh may take that row away.
    setActionsMenu(null);
    try {
      // Five endpoints, one table. The two row-backed ones supply the
      // rows, /admin/sessions supplies the live join, /admin/networks
      // supplies the capacity summary + the slug for an orphan row, and
      // /admin/session_log/sessions supplies the history join plus the
      // rows of subjects that no longer exist (#1158 item 4).
      // Failure of ANY collapses the whole render to the banner: a
      // half-built merge is worse than no table, because the operator
      // cannot tell which half is missing. That holds hardest for the
      // log: a silently dropped fetch would remove exactly the rows the
      // operator came for, and leave a table that looks complete.
      const [nextVisitors, nextCredentials, nextSessions, nextNetworks, nextLogSessions] =
        await Promise.all([
          adminListVisitors(t),
          adminListCredentials(t),
          adminListSessions(t),
          adminListNetworks(t),
          adminListSessionLogSessions(t),
        ]);
      setVisitors(nextVisitors);
      setCredentials(nextCredentials);
      setSessions(nextSessions);
      setNetworks(nextNetworks);
      setLogSessions(nextLogSessions);
    } catch (e) {
      setVisitors(null);
      setCredentials(null);
      setSessions(null);
      setNetworks(null);
      setLogSessions(null);
      const code = e instanceof ApiError ? e.code : "fetch_failed";
      setError(code);
    } finally {
      setLoading(false);
    }
  };

  const runAction = async (row: AdminSubjectRow, kind: ActionKind): Promise<void> => {
    const t = token();
    if (t === null) return;
    setError(null);
    try {
      await ACTION_FN[kind](t, row.key);
      setConfirmingKey(null);
      // Re-fetch rather than splice: every one of these verbs moves
      // live BEAM state (and disconnect on a user subject also moves
      // the DB connection_state), and the row STAYS either way — only
      // its live/DB columns flip. The server projection is the only
      // honest source for what they flipped to.
      await refresh();
    } catch (e) {
      // Prefix with the verb: three actions share this cell and a bare
      // `cannot_disconnect_self` would not say which one earned it.
      const code = e instanceof ApiError ? e.code : "request_failed";
      setError(`${kind}: ${code}`);
      setConfirmingKey(null);
    }
  };

  const runDelete = async (row: AdminSubjectRow): Promise<void> => {
    const t = token();
    const id = row.visitor?.visitor_id;
    if (t === null || id === undefined) return;
    setError(null);
    try {
      await adminDeleteVisitor(t, id);
      setConfirmingKey(null);
      setDetailKey(null);
      // Refetch, NOT splice: the verb is identity-wide, so it can take
      // sibling rows on other networks with it. Splicing the one row
      // the operator clicked would leave the others on screen as
      // phantoms.
      await refresh();
    } catch (e) {
      const code = e instanceof ApiError ? e.code : "delete_failed";
      setError(`delete: ${code}`);
      setConfirmingKey(null);
    }
  };

  useRefreshSlot({
    onRefresh: () => {
      void refresh();
    },
    busy: loading,
    label: "refresh sessions list",
    testId: "admin-sessions-refresh",
  });

  onMount(() => {
    void refresh();
  });

  return (
    <div class="admin-sessions-tab">
      <Show when={endedOpen()}>
        <AdminEndedSessionsPage rows={ended()} onBack={() => setEndedOpen(false)} />
      </Show>

      <Show when={!endedOpen()}>
        <div class="adm-scroll">
          <Show when={error() !== null}>
            <AdminError message={error() ?? ""} testId="admin-sessions-error" />
          </Show>

          <Show when={networks() !== null && (networks() ?? []).length > 0}>
            <AdminCard
              title="Capacity per network"
              subtitle="live_counts from the Registry — the same projection the admission policy uses"
              data-testid="admin-sessions-network-summary"
            >
              <AdminTable data-testid="admin-sessions-summary-table">
                <thead>
                  <tr>
                    <th>network</th>
                    <th>visitors</th>
                    <th>users</th>
                    <th>per-IP cap</th>
                  </tr>
                </thead>
                <tbody>
                  <For each={networks() ?? []}>
                    {(net) => (
                      <tr
                        class="admin-sessions-summary-row"
                        data-testid={`admin-sessions-summary-row-${net.slug}`}
                      >
                        <td class="adm-cell-title">{net.slug}</td>
                        <td
                          data-label="visitors"
                          data-testid={`admin-sessions-summary-visitors-${net.slug}`}
                        >
                          {net.live_counts.visitors}/
                          {renderCap(net.max_concurrent_visitor_sessions)}
                        </td>
                        <td
                          data-label="users"
                          data-testid={`admin-sessions-summary-users-${net.slug}`}
                        >
                          {net.live_counts.users}/{renderCap(net.max_concurrent_user_sessions)}
                        </td>
                        <td
                          data-label="per-ip"
                          data-testid={`admin-sessions-summary-per-ip-${net.slug}`}
                        >
                          {renderCap(net.max_per_ip)}
                        </td>
                      </tr>
                    )}
                  </For>
                </tbody>
              </AdminTable>
            </AdminCard>
          </Show>

          <Show when={!loaded() && error() === null}>
            <AdminEmpty message="loading…" />
          </Show>

          {/* #1224 — the card renders whenever the data loaded, empty list
            or not, and the empty state sits INSIDE it. The door to the
            ended sessions lives in this header, and a live list that
            happens to be empty is exactly when the operator most needs
            it: gating the card on the row count would take the door away
            with the rows. */}
          <Show when={loaded()}>
            <AdminCard
              hostsRefresh
              title="Sessions"
              subtitle="row-backed and strictly live — parked and failed subjects are listed too, with live state joined on"
              actions={
                <button
                  type="button"
                  class="adm-btn"
                  onClick={() => setEndedOpen(true)}
                  data-testid="admin-sessions-ended-open"
                >
                  Ended sessions ({ended().length})
                </button>
              }
              data-testid="admin-sessions-table-card"
            >
              <Show
                when={live().length > 0}
                fallback={
                  // "live", not "no sessions": the log may well remember
                  // sessions that ended, and they are one tap away.
                  <AdminEmpty message="no live sessions" testId="admin-sessions-empty" />
                }
              >
                {/* #1308 — the sort control. It lives above the table
                  rather than on the `<th>`s because only ONE of the three
                  keys vjt named has a column: `last active` and
                  `last joined` are facts of the drill-down, and giving
                  them columns is the very question (does an extra column
                  fit at every width?) the IP half of #1308 is still
                  waiting on a ruling for.

                  Inside the `live().length > 0` branch on purpose: an
                  empty list has nothing to order, and a live control over
                  no rows reads as broken. */}
                <div class="admin-sessions-sort" role="toolbar" aria-label="sort sessions">
                  <span class="admin-sessions-sort-label" aria-hidden="true">
                    sort
                  </span>
                  <For each={SESSION_SORT_KEYS}>
                    {(key) => (
                      <button
                        type="button"
                        class="adm-btn admin-sessions-sort-btn"
                        aria-pressed={sort().key === key}
                        onClick={() => cycleSort(key)}
                        data-testid={`admin-sessions-sort-${key}`}
                      >
                        {SESSION_SORT_LABEL[key]}
                        {/* The arrow is on the ACTIVE key only: an arrow
                          on every button would say three orders are in
                          force at once. `aria-pressed` says WHICH key is
                          on but not which way it points, so the direction
                          is spelled out for a screen reader rather than
                          left to a glyph it would read as punctuation. */}
                        <Show when={sort().key === key}>
                          <span class="admin-sessions-sort-arrow" aria-hidden="true">
                            {sort().direction === "asc" ? "▲" : "▼"}
                          </span>
                          <span class="sr-only">
                            {sort().direction === "asc" ? ", ascending" : ", descending"}
                          </span>
                        </Show>
                      </button>
                    )}
                  </For>
                </div>
                <AdminTable class="admin-sessions-table" data-testid="admin-sessions-table">
                  <thead>
                    <tr>
                      <th class="adm-table-grow">who</th>
                      <th>last seen</th>
                      <th>channels</th>
                      <th class="adm-table-sticky-actions">actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <For each={live()}>
                      {(row) => (
                        <>
                          <tr
                            class="admin-sessions-row"
                            data-testid={`admin-session-row-${row.key}`}
                          >
                            <td class="admin-session-who adm-cell-title">
                              <AdminRowName
                                alwaysOpenable
                                open={detailKey() === row.key}
                                onToggle={() =>
                                  setDetailKey(detailKey() === row.key ? null : row.key)
                                }
                                label={`details for ${renderWho(row)}`}
                                testId={`admin-session-details-${row.key}`}
                              >
                                {/* The two dictated lines are ONE block beside the
                                caret, not two siblings of it. Left as siblings
                                they are flex items of `.adm-row-expand`, and
                                the second line can only be produced by letting
                                that row wrap — which makes the layout depend on
                                nick length: a nick too long to sit beside the
                                caret pushes the whole identity onto its own
                                line, losing the caret's indent and breaking the
                                alignment the fixed-width badge exists to
                                create. Measured at 16px of drift. */}
                                <span class="admin-session-lines">
                                  <span class="admin-session-identity">
                                    {/* Fixed-width kind badge: "visitor" and
                                    "user" are different lengths, and an
                                    unpadded pair makes every nick start at
                                    a different x. */}
                                    <AdminBadge
                                      tone={row.subject_kind === "user" ? "info" : "neutral"}
                                      class="adm-badge--kind"
                                      ariaLabel={row.subject_kind}
                                    >
                                      {row.subject_kind}
                                    </AdminBadge>
                                    {/* No `deleted` badge (#1224, vjt: "no badge
                                    needed"). #1158 item 4 put one here
                                    because a log-only row sat in this list
                                    and would otherwise have read as a
                                    session that merely looks broken; those
                                    rows are on their own page now, so the
                                    badge would have nothing to mark. */}
                                    <span class="admin-session-nick">{renderLabel(row)}</span>
                                    {/* #1315 — the account behind a user
                                    row, beside the nick and never in
                                    place of it: services renaming a
                                    session to `GuestNNNNN` leaves a nick
                                    that identifies nobody, and the only
                                    other thing the row carried was a UUID
                                    that is not printed anywhere. Absent
                                    on every class with no account
                                    (visitor, orphan pid, log-only), where
                                    an empty element would read as an
                                    account we failed to fetch. */}
                                    <Show when={row.user_name}>
                                      {(name) => (
                                        <span class="admin-session-account" title="account">
                                          {name()}
                                        </span>
                                      )}
                                    </Show>
                                  </span>
                                  <span class="admin-session-network">
                                    {row.network_slug ?? `network ${row.network_id}`}
                                  </span>
                                </span>
                              </AdminRowName>
                            </td>
                            <td
                              class="admin-session-last-seen"
                              data-label="last seen"
                              data-testid={`admin-session-last-seen-${row.key}`}
                              title={row.last_seen_at ?? "no browser session on record"}
                            >
                              {renderLastSeen(row.last_seen_at)}
                            </td>
                            <td
                              data-label="channels"
                              data-testid={`admin-session-channels-${row.key}`}
                            >
                              {renderChannels(row)}
                            </td>
                            <td
                              class="admin-sessions-actions adm-table-sticky-actions"
                              data-label="actions"
                            >
                              <For each={cellActions(row, isAdminNarrow(), armedKind(row))}>
                                {(kind) => (
                                  <InlineConfirmButton
                                    idleLabel={ACTION_LABEL[kind]}
                                    confirmLabel={`Confirm ${kind}`}
                                    armed={confirmingKey() === confirmKey(row.key, kind)}
                                    onArm={() => setConfirmingKey(confirmKey(row.key, kind))}
                                    onConfirm={() => runAction(row, kind)}
                                    testId={`admin-session-${kind}-${row.key}`}
                                    extraClass={`${kind}-btn`}
                                  />
                                )}
                              </For>
                              <Show when={collapsed(row) && armedKind(row) === null}>
                                <button
                                  type="button"
                                  class="adm-btn"
                                  aria-haspopup="menu"
                                  aria-label={`actions for ${renderWho(row)}`}
                                  onClick={(e) => {
                                    const box = e.currentTarget.getBoundingClientRect();
                                    setActionsMenu({
                                      key: row.key,
                                      x: box.left,
                                      y: box.bottom,
                                    });
                                  }}
                                  data-testid={`admin-session-actions-menu-${row.key}`}
                                >
                                  Actions ▾
                                </button>
                              </Show>
                              {/* The way back out of an armed verb. On desktop
                              the sibling verb's idle button does this job —
                              arming one disarms the other — but the collapse
                              took the sibling away, and the button the
                              operator tapped to get here went with it. */}
                              <Show when={collapsed(row) && armedKind(row) !== null}>
                                <button
                                  type="button"
                                  class="adm-btn"
                                  onClick={() => setConfirmingKey(null)}
                                  data-testid={`admin-session-actions-cancel-${row.key}`}
                                >
                                  Cancel
                                </button>
                              </Show>
                              {/* An empty cell reads as a rendering bug. The
                              dash says the absence is the answer. */}
                              <Show when={rowActions(row).length === 0}>—</Show>
                            </td>
                          </tr>
                          <Show when={detailKey() === row.key}>
                            <AdminDetailPanel
                              title={renderWho(row)}
                              subtitle="the rest of the record"
                              onClose={() => setDetailKey(null)}
                              closeLabel="close session details"
                              columns={SESSION_COLUMNS}
                              data-testid={`admin-session-detail-${row.key}`}
                            >
                              <AdminFacts facts={detailFacts(row)} />
                              <Show when={row.visitor !== null}>
                                <div class="adm-danger-strip">
                                  {/* Named for what it destroys. The verb is
                                  identity-wide: it deletes the visitor
                                  and every one of its network rows, not
                                  the row this panel hangs off. */}
                                  <span class="adm-danger-note">
                                    deletes the whole visitor identity, on every network
                                  </span>
                                  <InlineConfirmButton
                                    idleLabel="Delete visitor"
                                    confirmLabel="Confirm delete visitor"
                                    armed={confirmingKey() === confirmKey(row.key, "delete")}
                                    onArm={() => setConfirmingKey(confirmKey(row.key, "delete"))}
                                    onConfirm={() => runDelete(row)}
                                    testId={`admin-session-delete-${row.key}`}
                                    extraClass="delete-btn"
                                  />
                                </div>
                              </Show>
                            </AdminDetailPanel>
                          </Show>
                        </>
                      )}
                    </For>
                  </tbody>
                </AdminTable>
              </Show>
            </AdminCard>
          </Show>
        </div>
      </Show>

      {/* #1157 — the collapsed cell's dropdown. Reuses the shell the
          long-press menus use (portal, backdrop, Escape, measured
          flip/clamp) rather than growing a second popover convention in
          the console: what differs between them is the item list, which
          is exactly the part this supplies.

          Picking an item ARMS the verb — the shell closes itself on an
          action — and the row's own confirmation is what runs it. */}
      <Show when={actionsMenu()}>
        {(menu) => (
          <Show when={live().find((row) => row.key === menu().key)}>
            {(row) => (
              <ContextMenu
                position={{ x: menu().x, y: menu().y }}
                onClose={() => setActionsMenu(null)}
                items={rowActions(row()).map((kind) => ({
                  label: ACTION_LABEL[kind],
                  enabled: true,
                  action: () => setConfirmingKey(confirmKey(row().key, kind)),
                }))}
              />
            )}
          </Show>
        )}
      </Show>
    </div>
  );
};

function renderLabel(row: AdminSubjectRow): string {
  if (row.label !== null) return row.label;
  // `null` here means an orphan pid whose DB row is gone — the one
  // label-less class this list still holds. (The log-only class, whose
  // entry can predate the session having a nick, is on the sub-page and
  // has its own renderer for the same reason.) Say which rather than
  // rendering a blank: it is the divergence, not a missing value.
  return `${row.subject_id.slice(0, 8)} (no DB row)`;
}

function renderWho(row: AdminSubjectRow): string {
  return `${row.subject_kind}: ${renderLabel(row)}`;
}

// An unknown count is NOT zero. `null` means either no pid or an
// introspection timeout, and "0 chan" would read as a connected session
// sitting in no channels — a different, actionable fact.
function renderChannels(row: AdminSubjectRow): string {
  const n = channelCount(row);
  if (n === null) return row.live === null ? "—" : "?";
  return String(n);
}

// Compact relative time. `null` → em-dash. A future timestamp (host vs
// jail clock skew) renders "now" rather than a negative age.
function renderLastSeen(iso: string | null): string {
  if (iso === null) return "—";
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return "?";
  const deltaMs = Date.now() - then;
  if (deltaMs < 0) return "now";
  const s = Math.floor(deltaMs / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

function renderUpstream(row: AdminSubjectRow): string {
  const up = row.upstream;
  if (up === null || up.peer_address === null) return "—";
  const hostport = up.peer_port === null ? up.peer_address : `${up.peer_address}:${up.peer_port}`;
  return up.peer_name === null ? hostport : `${up.peer_name} (${hostport})`;
}

function renderExpires(row: AdminSubjectRow): string {
  const v = row.visitor;
  if (v === null) return "—";
  if (v.identified) return "indefinite (NickServ)";
  if (v.expires_at === null) return "indefinite (legacy)";
  const at = Date.parse(v.expires_at);
  if (Number.isNaN(at)) return "?";
  const days = Math.ceil((at - Date.now()) / 86_400_000);
  return days <= 0 ? "expired" : `in ${days}d`;
}

// The last thing the lifecycle log saw this session do. `null` is NOT
// "this session never ran": the log is a bounded global ring written
// from an async cast, so it forgets, and saying so is the honest render.
function renderLastEvent(row: AdminSubjectRow): string {
  const e = row.last_event;
  if (e === null) return "nothing logged (bounded ring — not proof it never ran)";
  return `${e.event} · ${e.at}`;
}

function detailFacts(row: AdminSubjectRow): { label: string; value: string }[] {
  const facts: { label: string; value: string }[] = [
    // DB intent and live pid are separate sources of truth and both
    // render — `live: BEAM has no pid` against `connection: connected`
    // IS the U-0 divergence signal, and computing one from the other
    // would erase it.
    { label: "connection", value: row.connection_state ?? "no DB row" },
    { label: "live", value: renderLive(row) },
    // #1308 — the source address, and the reason it sits directly above
    // `upstream`: the two are the opposite ends of one session and the
    // pair is only readable together. This one is where the BROWSER came
    // from; `upstream` is the IRC server the bouncer dialled out to.
    //
    // Unconditional, unlike the `visitors.ip` fact it replaces: that one
    // lived inside the visitor-only block, so a user row showed no
    // address anywhere, and per-session is exactly the grain a user row
    // can answer (vjt, 2026-08-14).
    { label: "ip", value: row.session_ip ?? "—" },
    // NO `network` fact. #1223 item 1 reported the panel repeating it,
    // and unlike the Users/Credentials repeats this one is not the
    // `.adm-col-detail` specificity defect: Sessions drops no column at
    // any width, and `.admin-session-network` prints the slug under the
    // nick on DESKTOP too. Two defects, one symptom — raising the CSS
    // selector would not have touched this one, and the second line of
    // the identity block is dictated (vjt, 2026-08-09), so the panel is
    // the copy that goes.
    { label: "upstream", value: renderUpstream(row) },
    // #1158 item 4 — the third source, and the only one that says
    // anything at all about a session that is over.
    { label: "last event", value: renderLastEvent(row) },
  ];

  if (row.last_event?.event === "disconnected") {
    facts.push({ label: "ended", value: renderEnded(row.last_event) });
  }

  // No `record: session log only` fact any more (#1224): the panel that
  // carried it belonged to a row this list no longer holds, and on the
  // sub-page the sentence is the page's premise rather than a per-row
  // remark — it lives in the card subtitle there.

  if (row.live !== null) {
    facts.push(
      { label: "mailbox", value: String(row.live.mailbox_len) },
      { label: "memory", value: `${Math.round(row.live.memory_bytes / 1024)} KB` },
    );
    if (row.live.introspection_degraded.length > 0) {
      facts.push({
        label: "degraded",
        value: `⚠ ${row.live.introspection_degraded.join(", ")}`,
      });
    }
  }

  if (row.visitor !== null) {
    facts.push(
      { label: "expires", value: renderExpires(row) },
      { label: "joined", value: row.visitor.inserted_at },
    );
  }

  return facts;
}

function renderLive(row: AdminSubjectRow): string {
  if (row.live === null) return "BEAM has no pid";
  return row.live.alive ? "alive" : "pid registered but dead";
}

export default AdminSessionsTab;
